import { createContext, useContext, useState, ReactNode } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { SmartFAB } from "@/components/SmartFAB";
import { QuickAddSheet } from "@/components/QuickAddSheet";
import { UnifiedFoodLogDialog } from "@/components/UnifiedFoodLogDialog";
import { CommandPalette } from "@/components/CommandPalette";
import { useLocation } from "react-router-dom";

interface FoodLogContextType {
  openQuickAdd: () => void;
  openFoodLog: (foodId: string, foodName: string) => void;
  openCommandPalette: () => void;
}

const FoodLogContext = createContext<FoodLogContextType | undefined>(undefined);

// Routes where SmartFAB should be hidden
const HIDDEN_ROUTES = ["/auth", "/welcome", "/privacy-policy", "/terms-of-service", "/campaign", "/admin", "/content-management"];

export function FoodLogProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const location = useLocation();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [foodLogOpen, setFoodLogOpen] = useState(false);
  const [selectedFood, setSelectedFood] = useState<{ id: string; name: string } | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const openQuickAdd = () => setQuickAddOpen(true);
  const openCommandPalette = () => setPaletteOpen(true);

  const openFoodLog = (foodId: string, foodName: string) => {
    setSelectedFood({ id: foodId, name: foodName });
    setFoodLogOpen(true);
  };

  const handleFoodSelect = (foodId: string, foodName: string) => {
    setQuickAddOpen(false);
    openFoodLog(foodId, foodName);
  };

  const handleQuickAdd = (foodId: string, foodName: string) => {
    openFoodLog(foodId, foodName);
  };

  // Check if SmartFAB should be visible
  const shouldShowFAB = user && !HIDDEN_ROUTES.includes(location.pathname);

  return (
    <FoodLogContext.Provider value={{ openQuickAdd, openFoodLog, openCommandPalette }}>
      {children}
      
      {shouldShowFAB && (
        <SmartFAB
          onQuickAdd={handleQuickAdd}
          onOpenFullSheet={openQuickAdd}
        />
      )}

      <QuickAddSheet
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        onFoodSelect={handleFoodSelect}
      />

      {selectedFood && (
        <UnifiedFoodLogDialog
          open={foodLogOpen}
          onOpenChange={(open) => {
            setFoodLogOpen(open);
            if (!open) setSelectedFood(null);
          }}
          foodId={selectedFood.id}
          foodName={selectedFood.name}
        />
      )}

      {user && (
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      )}
    </FoodLogContext.Provider>
  );
}

const fallbackContext: FoodLogContextType = {
  openQuickAdd: () => {},
  openFoodLog: () => {},
  openCommandPalette: () => {},
};

export function useFoodLog() {
  const context = useContext(FoodLogContext);
  return context ?? fallbackContext;
}
